import {
  useDeleteCurrentUserMutation,
  useGetCurrentUser,
} from 'gcs-common/clients/api/entities/current-user/current-user-api';
import LoadingIndicator from 'gcs-common/components/LoadingIndicator/LoadingIndicator';
import { LEGACY_EVENTS } from 'gcs-common/config/googleAnalyticsLegacyConfig';
import { PHONE_OR_MAIL_REGEX } from 'gcs-common/constants/formValidation';
import { cleanPhoneNumber } from 'gcs-common/helper/formHelper';
import { getIsAgent } from 'gcs-common/slices/currentUser/currentUserSelectors';
import { trackGALegacyEvent } from 'gcs-common/slices/googleAnalytics/googleAnalyticsThunks';
import PropTypes from 'prop-types';
import { type ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../Button/Button';
import ModalWithCloseButton from '../../PopUpModal/ModalWithCloseButton/ModalWithCloseButton';
import TextInput from '../../TextInput/TextInput';
import styles from './styles.module.scss';

const AccountSettings = ({ isOpen, onRequestClose }: { isOpen: boolean; onRequestClose: () => void }) => {
  const [showDeletionConfirmationModal, setShowDeletionConfirmationModal] = useState(false);
  const [userIdentifierInput, setUserIdentifierInput] = useState<string | undefined>(undefined);

  const { data: currentUser } = useGetCurrentUser();
  const deleteCurrentUserMutation = useDeleteCurrentUserMutation();
  const isDeleting = deleteCurrentUserMutation.isPending;
  const hasDeletionError = deleteCurrentUserMutation.isError;

  const dispatch = useDispatch();
  const isAgent = useSelector(getIsAgent);

  const onDeleteConfirmationOpened = useCallback(() => {
    setShowDeletionConfirmationModal(true);
    dispatch(trackGALegacyEvent(LEGACY_EVENTS.SHOW_DELETE_CONFIRMATION()));
  }, [dispatch]);

  const onDeleteAccountClicked = () => {
    deleteCurrentUserMutation.mutate();
  };

  const onDeleteConfirmationClosed = useCallback(() => {
    setShowDeletionConfirmationModal(false);
    onRequestClose();
  }, [onRequestClose]);

  const onUserIdentifierChanged = (e: ChangeEvent<HTMLInputElement>) => {
    setUserIdentifierInput(e.target.value);
  };

  const inputValid = useMemo(() => {
    const phoneNumber = currentUser?.phoneNumber;
    if (phoneNumber && userIdentifierInput) {
      if (cleanPhoneNumber(phoneNumber) === cleanPhoneNumber(userIdentifierInput)) {
        return true;
      }
    }
    return currentUser?.email?.toLowerCase() === userIdentifierInput?.toLowerCase();
  }, [currentUser, userIdentifierInput]);

  const isInvalidEmailOrPhone = useMemo(() => {
    return !inputValid && userIdentifierInput && PHONE_OR_MAIL_REGEX.test(userIdentifierInput);
  }, [inputValid, userIdentifierInput]);

  const deletionConfirmationBody = useMemo(
    () => (
      <>
        <div className={styles.title}>BEEM ACCOUNT DATEN LÖSCHEN</div>
        {isDeleting ? (
          <LoadingIndicator />
        ) : (
          <div className={styles.contentContainer}>
            <strong>Sie sind dabei, Ihren BEEM Account dauerhaft zu löschen.</strong>
            <p>
              <strong>Alle verknüpften Daten werden dabei gelöscht.</strong>
            </p>
            <strong>Sie werden dann ausgeloggt.</strong>
          </div>
        )}
        <div className={styles.buttonContainer}>
          <Button disabled={isDeleting} className={styles.buttonError} onClick={onDeleteAccountClicked}>
            Account löschen
          </Button>
          <Button disabled={isDeleting} className={styles.confirmationButton} onClick={onDeleteConfirmationClosed}>
            Abbrechen
          </Button>
        </div>
        {hasDeletionError && (
          <div className={styles.inputError}>
            Beim löschen is ein Fehler aufgetreten. Bitte versuchen Sie es noch einmal.
          </div>
        )}
      </>
    ),
    [hasDeletionError, isDeleting, onDeleteAccountClicked, onDeleteConfirmationClosed],
  );

  const accountDeletionBody = useMemo(
    () => (
      <>
        <div className={styles.title}>BEEM ACCOUNT DATEN LÖSCHEN</div>
        <div className={styles.contentContainer}>
          <p>
            <strong>Wenn Sie Ihren Account löschen, werden folgende Daten dauerhaft gelöscht:</strong>
          </p>
          <ul>
            <li>- BEEM Accountinformationen und Profilbild</li>
            <li>- Ihr kompletter Nachrichtenverlauf in allen Chats</li>
          </ul>
          <p>
            Sie werden aus allen Kanälen und Gruppenchats entfernt.{' '}
            <strong>Eine Wiederherstellung der Daten ist nachdem Löschen nicht mehr möglich.</strong>
          </p>
        </div>
        <div className={styles.inputContainer}>
          <div className={styles.title}>{!isAgent && 'MOBILNUMMER ODER'} E-MAIL ADRESSE EINGEBEN*</div>
          <TextInput value={userIdentifierInput} className={styles.input} onChange={onUserIdentifierChanged} />
          {isInvalidEmailOrPhone && (
            <div className={styles.inputError}>
              {!isAgent && 'Mobilnummer oder'} E-Mail Adresse passt nicht zu den Accountinformationen
            </div>
          )}
        </div>
        <div className={styles.buttonContainer}>
          <Button disabled={!inputValid} onClick={onDeleteConfirmationOpened} className={styles.button}>
            Account löschen
          </Button>
        </div>
      </>
    ),
    [
      inputValid,
      isAgent,
      isInvalidEmailOrPhone,
      onDeleteConfirmationOpened,
      onUserIdentifierChanged,
      userIdentifierInput,
    ],
  );

  return (
    <ModalWithCloseButton label="Account Einstellungen" onRequestClose={onRequestClose} isOpen={isOpen}>
      {showDeletionConfirmationModal ? deletionConfirmationBody : accountDeletionBody}
    </ModalWithCloseButton>
  );
};

AccountSettings.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onRequestClose: PropTypes.func.isRequired,
};

export default AccountSettings;
